import cx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as styles from "./NumberPicker.module.scss";

export interface NumberPickerProps {
	fieldType?: "number";
	// The label for the number picker
	label: string;
	// The legend for the number picker
	legend?: string;
	// The value of the number picker
	value: string;
	// The function to call when the number is changed
	onChange: (value: number) => void;
	// The minimum value for the number picker
	min?: number;
	// The maximum value for the number picker
	max?: number;

	// The increment value for the number picker
	increment?: number;

	// If the number picker is hidden
	hidden?: boolean;

	// If the number picker is disabled
	disabled?: boolean;
	editableButton?: React.ReactNode;
	className?: string;
	name: string;
}

export const NumberPicker: React.FC<NumberPickerProps> = ({
	label,
	onChange,
	max,
	hidden,
	value: strValue = "0",
	disabled,
	className,
	name,
	legend,
	min = 0,
	increment = 1,
}) => {
	const getSafeNumber = useCallback(() => {
		const val =
			Number.parseInt(strValue, 10) < min
				? min
				: Number.parseInt(strValue, 10) > max
					? max
					: Number.parseInt(strValue, 10);

		return Number.isNaN(val) ? min : val;
	}, [min, max, strValue]);

	const [hasInitialised, setHasInitialised] = useState(false);
	const [value, setValue] = useState(getSafeNumber());

	const [key, setKey] = useState(0);

	// This is a hack to force the input to re-render first time and load the incoming value.
	useEffect(() => {
		setKey((prev) => prev + 1);
	}, []);

	useEffect(() => {
		if (!hasInitialised && !hidden) {
			onChange(value);
			setHasInitialised(true);
			return;
		}
	}, [hidden, hasInitialised, onChange, value]);

	useEffect(() => {
		if (!hidden) {
			setValue(getSafeNumber());
		}
	}, [hidden, getSafeNumber]);

	// If the number picker is disabled, set the value to undefined to clear the field
	// biome-ignore lint/correctness/useExhaustiveDependencies: Only run on disabled
	useEffect(() => {
		if (disabled) onChange("DO_DELETE" as unknown as number);
		else onChange(value);
	}, [disabled]);
	// Number Picker
	// Is a number input
	// Has a label
	// Has a before and after button allowing the user to increase or decrease the number
	// Has a min and max value and the buttons are disabled when the min or max value is reached

	const NumberInput = useMemo(
		() => (
			<div
				key={`${hidden}-${label}-${key}`}
				className={cx(styles.numberPicker, className)}
				style={{ display: hidden ? "none" : undefined }}
			>
				<label className={styles.numberPickerLabel} htmlFor={name}>
					{label}
				</label>
				<span className={styles.numberPickerInputContainer}>
					<button
						type="button"
						className={styles.decrementButton}
						onClick={() => {
							const nextValue = value - increment;
							const roundedValue = Math.ceil(nextValue / increment) * increment;
							onChange(roundedValue < min ? min : roundedValue);
						}}
						disabled={disabled || (min !== undefined && value <= min)}
						role="spinbutton"
						aria-valuemax={max}
						aria-valuemin={min}
						aria-valuenow={value}
						aria-label={`Decrease value by ${increment}`}
						hidden={hidden}
					>
						{"\u2212"}
					</button>
					<input
						name={name}
						type="number"
						step={increment}
						className={styles.numberPickerInput}
						value={value}
						onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
						min={min}
						max={max}
						hidden={hidden}
						disabled={disabled}
					/>
					<button
						type="button"
						className={styles.incrementButton}
						onClick={() => {
							const nextValue = value + increment;
							const roundedValue = Math.ceil(nextValue / increment) * increment;
							onChange(roundedValue > max ? max : roundedValue);
						}}
						disabled={disabled || (max !== undefined && value >= max)}
						role="spinbutton"
						aria-valuemax={max}
						aria-valuemin={min}
						aria-valuenow={value}
						aria-label={`Increase value by ${increment}`}
						hidden={hidden}
					>
						+
					</button>
				</span>
			</div>
		),
		[
			className,
			disabled,
			hidden,
			key,
			label,
			max,
			min,
			name,
			onChange,
			value,
			increment,
		],
	);
	if (legend) {
		return (
			<fieldset className={styles.numberPickerFieldset}>
				<legend
					className={styles.numberPickerLegend}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
					dangerouslySetInnerHTML={{
						__html: legend,
					}}
				/>
				{NumberInput}
			</fieldset>
		);
	}
	return NumberInput;
};
